import React, { useEffect, useState } from "react";
import patientService from '../../../../../services/patient.service';
import { useEyeconxStore } from '../../../../../store/eyeconxStore';
import { toast } from "react-toastify";

const mediumIcons = {
  SMS: "bi-chat-text-fill text-primary",
  EMAIL: "bi-envelope-fill text-info",
  CALL: "bi-telephone-fill text-success",
  UNKNOWN: "bi-question-circle text-secondary",
};

export default function PatientActivity({ patientId }) {
  const clinicId = useEyeconxStore((state) => state.user.clinicId);

  const [loading, setLoading] = useState(false);
  const [activityList, setActivityList] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (patientId) loadActivity(0);
  }, [patientId]);

  const loadActivity = async (pageNumber = 0) => {
    try {
      setLoading(true);
      const res = await patientService.getPatientActivities(
        clinicId,
        patientId,
        pageNumber
      );

      const data = res.data;

      setActivityList(data.content || []);
      setTotalPages(data.totalPages || 1);
      setPage(pageNumber);
    } catch (err) {
      toast.error("Failed to load activity");
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (medium) => {
    if (!medium) return mediumIcons.UNKNOWN;
    return mediumIcons[medium] || mediumIcons.UNKNOWN;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "SENT":
        return "badge bg-success";
      case "FAILED":
        return "badge bg-danger";
      case "QUEUED":
        return "badge bg-warning text-dark";
      default:
        return "badge bg-secondary";
    }
  };

  return (
    <div className="card shadow-sm border-0">
      <div className="card-header bg-white">
        <h5 className="fw-semibold mb-0">
          <i className="bi bi-activity text-primary me-2"></i>
          Patient Activity
        </h5>
      </div>

      <div className="card-body">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary"></div>
          </div>
        ) : activityList.length === 0 ? (
          <div className="text-center text-muted py-4">
            <i className="bi bi-inbox fs-1 opacity-50"></i>
            <p className="mt-2">No activity found</p>
          </div>
        ) : (
          <ul className="list-group list-group-flush">
            {activityList.map((a) => (
              <li
                key={a.id}
                className="list-group-item px-0 py-3 d-flex"
                style={{ borderBottom: "1px solid #eee" }}
              >
                {/* Icon */}
                <div className="me-3">
                  <i className={`${getIcon(a.mediumType)} fs-3`}></i>
                </div>

                {/* Main content */}
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between">
                    <h6 className="fw-bold mb-1">{a.activityName}</h6>

                    <span className={getStatusBadge(a.status)}>
                      {a.status}
                    </span>
                  </div>

                  <div className="text-muted small">
                    <div>
                      <i className="bi bi-clock me-1"></i>
                      {a.createdAt}
                    </div>

                    <div>
                      <i className="bi bi-person me-1"></i>
                      {a.patientName}
                    </div>

                    <div>
                      <i className="bi bi-send-check me-1"></i>
                      Sent to: <strong>{a.receiver}</strong>
                    </div>

                    <div>
                      <i className="bi bi-tag me-1"></i>
                      Category: {a.category}
                    </div>
                  </div>

                  {/* Optional content block */}
                  {a.content && (
                    <div className="mt-2 p-3 bg-light rounded small">
                      {a.content}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="card-footer bg-white d-flex justify-content-between">
          <button
            className="btn btn-sm btn-outline-secondary"
            disabled={page === 0}
            onClick={() => loadActivity(page - 1)}
          >
            <i className="bi bi-arrow-left"></i> Previous
          </button>

          <span className="text-muted small">
            Page {page + 1} of {totalPages}
          </span>

          <button
            className="btn btn-sm btn-outline-secondary"
            disabled={page + 1 >= totalPages}
            onClick={() => loadActivity(page + 1)}
          >
            Next <i className="bi bi-arrow-right"></i>
          </button>
        </div>
      )}
    </div>
  );
}
